// One AudioContext for the whole app: the voice plays through it, and the
// recorder meters and resamples with it.
//
// WebAudio rather than an <audio> element because of iOS: a context resumed
// inside a tap stays allowed to play afterwards, and the spoken answer arrives
// well after the tap that asked for it (record → transcribe → synthesize).

let context = null;
let output = null;    // the gain everything the voice plays goes through
let volume = 1;
let playing = null;   // { source, done }

export function audioContext() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!context) { try { context = new AC(); } catch { return null; } }
  if (context.state === 'suspended') context.resume().catch(() => {});
  return context;
}

/** How loud the voice is, 0 (muted) to 1. Takes effect at once, on a clip already playing too. */
export function setVolume(level) {
  volume = Math.max(0, Math.min(1, Number(level) || 0));
  if (output) output.gain.value = volume;
}

/** Call from a tap: iOS keeps the context suspended until then. */
export const unlockAudio = () => { audioContext(); };

export function stopPlayback() {
  if (!playing) return;
  const { source, done } = playing;
  playing = null;
  try { source.stop(); } catch {}
  done(false);
}

/**
 * Play encoded audio (mp3 / wav bytes). Resolves true when it played to the
 * end, false when it was cut off by stopPlayback() or a newer play().
 */
export async function play(bytes) {
  const ctx = audioContext();
  if (!ctx) throw new Error('audio_unsupported');
  stopPlayback();
  // decodeAudioData detaches the buffer it is given, and the caller caches it.
  const buffer = await ctx.decodeAudioData(bytes.slice(0));
  stopPlayback();
  return new Promise((resolve) => {
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    if (!output) {
      output = ctx.createGain();
      output.gain.value = volume;
      output.connect(ctx.destination);
    }
    source.connect(output);
    const mine = { source, done: resolve };
    source.onended = () => { if (playing === mine) { playing = null; resolve(true); } };
    playing = mine;
    source.start();
  });
}
